/**
 * Notification-rule args schema — the client-side interpreter.
 *
 * The backend owns the schema (`backend/demo/lib/events/rule_schema.py`) and
 * serves it at `…/notification_rulesets/rule_schema/`; this file interprets it
 * so the editors flag invalid rules before the save roundtrip. The server stays
 * authoritative: it runs the same checks plus server-only hooks (template
 * compile, tag/ruleset lookups) and rejects new or changed rows.
 *
 * SELF-CONTAINED ON PURPOSE: the dashboard keeps a VERBATIM copy at
 * `dashboard/src/notifications/ruleSchema.ts` (TypeScript 4.7 — keep the syntax
 * conservative, no imports). Both copies run the golden cases in
 * `apiv2/notification-rules/rule-args-cases.json`, which the backend's own
 * interpreter also passes — change all three together.
 *
 * Anything this interpreter does not know — a newer vocabulary `version`, an
 * unknown node type or check kind — is SKIPPED, never reported: an old tab must
 * not block a save the server would accept.
 */

/* eslint-disable @typescript-eslint/no-explicit-any */

export const SUPPORTED_SCHEMA_VERSION = 1;

export type Severity = 'error' | 'warning';

export interface RuleIssue {
  /** `args.comparison`, `args.fields.0.op`, `tpl`, `nested_ruleset`, … */
  path: string;
  code: string;
  severity: Severity;
  effect: string | null;
  message: string;
}

export interface RuleArgsSchema {
  version: number;
  revision: string;
  messages: Record<string, string>;
  expected: Record<string, string>;
  format_hints: Record<string, string>;
  effects: Record<string, string>;
  row: {
    types: Record<string, string>;
    ops: Record<string, string>;
    value_optional_ops: number[];
    tpl_ruleset: number;
    max_field_length: number;
    max_value_length: number;
    messages: Record<string, string>;
  };
  templates: Record<
    string,
    { label: string; default_args: unknown; args: SchemaNode; notice?: string }
  >;
}

export type SchemaNode = Record<string, any>;

type Json = any;

const MISSING = undefined;

const NODE_KINDS: Record<string, string[]> = {
  object: ['object'],
  list: ['list'],
  string: ['string'],
  boolean: ['boolean'],
  integer: ['number'],
  number: ['number'],
  number_like: ['number', 'string'],
  int_like: ['number', 'string'],
  id_ref: ['number', 'string', 'object'],
  datetime: ['string'],
  date: ['string'],
  time: ['string'],
  any: ['null', 'boolean', 'number', 'string', 'list', 'object'],
};

// ASCII digits only, same as the backend (Python's \d matches any Unicode digit).
const DATETIME_RE =
  /^([0-9]{4})-([0-9]{2})-([0-9]{2})[T ]([0-9]{2}):([0-9]{2})(?::([0-9]{2})(?:\.[0-9]{1,9})?)?(Z|[+-][0-9]{2}:?[0-9]{2})?$/;
const DATE_RE = /^([0-9]{4})-([0-9]{2})-([0-9]{2})$/;
const TIME_RE = /^([0-9]{2}):([0-9]{2})(?::([0-9]{2})(?:\.[0-9]{1,9})?)?$/;
const INT_LIKE_RE = /^[+-]?[0-9]+$/;
const NUMBER_LIKE_RE = /^[+-]?(?:[0-9]+(?:\.[0-9]*)?|\.[0-9]+)$/;

// ---------------------------------------------------------------------------
// JSON helpers
// ---------------------------------------------------------------------------

function isNum(v: Json): boolean {
  return typeof v === 'number' && isFinite(v);
}

function isObj(v: Json): boolean {
  return v !== null && typeof v === 'object' && !Array.isArray(v);
}

function kindOf(v: Json): string {
  if (v === null || v === undefined) return 'null';
  if (typeof v === 'boolean') return 'boolean';
  if (isNum(v)) return 'number';
  if (typeof v === 'string') return 'string';
  if (Array.isArray(v)) return 'list';
  if (isObj(v)) return 'object';
  return 'other';
}

/** JSON equality: key order irrelevant, list order relevant, booleans ≠ numbers. */
export function jsonEqual(a: Json, b: Json): boolean {
  const aNil = a === null || a === undefined;
  const bNil = b === null || b === undefined;
  if (aNil || bNil) return aNil && bNil;
  if (typeof a === 'boolean' || typeof b === 'boolean') return a === b;
  if (typeof a === 'number' || typeof b === 'number') return isNum(a) && isNum(b) && a === b;
  if (typeof a === 'string' || typeof b === 'string') return a === b;
  if (Array.isArray(a) && Array.isArray(b)) {
    return a.length === b.length && a.every((x: Json, i: number) => jsonEqual(x, b[i]));
  }
  if (isObj(a) && isObj(b)) {
    const ka = Object.keys(a);
    const kb = Object.keys(b);
    if (ka.length !== kb.length) return false;
    return ka.every((k) => Object.prototype.hasOwnProperty.call(b, k) && jsonEqual(a[k], b[k]));
  }
  return false;
}

function getPath(obj: Json, path: string): Json {
  let cur = obj;
  for (const part of path.split('.')) {
    if (isObj(cur) && Object.prototype.hasOwnProperty.call(cur, part)) cur = cur[part];
    else return MISSING;
  }
  return cur;
}

function truthy(v: Json): boolean {
  if (v === undefined || v === null) return false;
  if (typeof v === 'boolean') return v;
  if (isNum(v)) return v !== 0;
  if (typeof v === 'string' || Array.isArray(v)) return v.length > 0;
  if (isObj(v)) return Object.keys(v).length > 0;
  return false;
}

export function evalCond(cond: SchemaNode, ctx: Json): boolean {
  if ('all' in cond) return (cond.all as SchemaNode[]).every((c) => evalCond(c, ctx));
  if ('any' in cond) return (cond.any as SchemaNode[]).some((c) => evalCond(c, ctx));
  if ('not' in cond) return !evalCond(cond.not, ctx);
  const v = getPath(ctx, cond.path);
  if ('eq' in cond) return v !== undefined && jsonEqual(v, cond.eq);
  if ('in' in cond) return v !== undefined && (cond.in as Json[]).some((x) => jsonEqual(v, x));
  if ('truthy' in cond) return truthy(v) === Boolean(cond.truthy);
  if ('present' in cond) return (v !== undefined && v !== null) === Boolean(cond.present);
  if ('min_items' in cond) return Array.isArray(v) && v.length >= cond.min_items;
  return false;
}

/** `{name}` placeholders — the one substitution rule all interpreters share. */
export function render(template: string | undefined, params: Record<string, Json>): string {
  return (template ?? '').replace(/\{([a-z_]+)\}/g, (m, name: string) =>
    params[name] === undefined ? m : String(params[name]),
  );
}

/** A value inside a message: text verbatim, anything else as JSON. */
function show(v: Json): string {
  return typeof v === 'string' ? v : JSON.stringify(v === undefined ? null : v);
}

function resolveInt(v: Json): number | null {
  if (isNum(v) && Number.isInteger(v)) return v;
  if (typeof v === 'string' && INT_LIKE_RE.test(v)) return parseInt(v, 10);
  return null;
}

function resolveNumber(v: Json): number | null {
  if (isNum(v)) return v;
  if (typeof v === 'string' && NUMBER_LIKE_RE.test(v)) return parseFloat(v);
  return null;
}

function intOrNull(v: Json): number | null {
  return typeof v === 'boolean' ? null : resolveInt(v);
}

function fmtNum(v: number): string {
  return String(v);
}

function isLeap(y: number): boolean {
  return (y % 4 === 0 && y % 100 !== 0) || y % 400 === 0;
}

function daysIn(y: number, m: number): number {
  return [31, isLeap(y) ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31][m - 1] ?? 0;
}

/** Comparable value for a valid datetime/date/time string, else null.
 *  datetime → [aware, seconds] (aware in UTC seconds, naive as wall clock). */
function parseDt(kind: string, s: Json): any {
  if (typeof s !== 'string') return null;
  if (kind === 'date') {
    const m = DATE_RE.exec(s);
    if (!m) return null;
    const y = Number(m[1]);
    const mo = Number(m[2]);
    const d = Number(m[3]);
    if (y < 1 || mo < 1 || mo > 12 || d < 1 || d > daysIn(y, mo)) return null;
    return y * 10000 + mo * 100 + d;
  }
  if (kind === 'time') {
    const m = TIME_RE.exec(s);
    if (!m) return null;
    const h = Number(m[1]);
    const mi = Number(m[2]);
    const sec = m[3] ? Number(m[3]) : 0;
    if (h > 23 || mi > 59 || sec > 59) return null;
    return h * 3600 + mi * 60 + sec;
  }
  const m = DATETIME_RE.exec(s);
  if (!m) return null;
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const d = Number(m[3]);
  const h = Number(m[4]);
  const mi = Number(m[5]);
  const sec = m[6] ? Number(m[6]) : 0;
  const tz = m[7];
  if (y < 1 || mo < 1 || mo > 12 || d < 1 || d > daysIn(y, mo)) return null;
  if (h > 23 || mi > 59 || sec > 59) return null;
  const dt = new Date(0);
  dt.setUTCFullYear(y, mo - 1, d);
  dt.setUTCHours(h, mi, sec, 0);
  let total = dt.getTime() / 1000;
  if (tz === undefined) return [false, total];
  if (tz !== 'Z') {
    const sign = tz.charAt(0) === '+' ? 1 : -1;
    const digits = tz.slice(1).replace(':', '');
    const offH = +digits.slice(0, 2);
    const offM = +digits.slice(2);
    if (offH > 23 || offM > 59) return null;
    total -= sign * (offH * 3600 + offM * 60);
  }
  return [true, total];
}

function uniqueKey(items: SchemaNode, item: Json): Json {
  const t = items.type;
  if (t === 'id_ref') return resolveInt(isObj(item) ? item.id : item);
  if (t === 'int_like') return resolveInt(item);
  if (t === 'one_of') {
    const resolved = resolveInt(isObj(item) ? item.id : item);
    return resolved !== null ? resolved : item;
  }
  return item;
}

// ---------------------------------------------------------------------------
// Interpreter
// ---------------------------------------------------------------------------

class Run {
  issues: RuleIssue[] = [];
  bad = new Set<string>();

  constructor(private readonly schema: RuleArgsSchema) {}

  add(
    path: string,
    code: string,
    node: SchemaNode,
    message: string | undefined,
    effect?: string | null,
    severity?: Severity,
  ) {
    const sev: Severity = severity || node.severity || 'error';
    const eff = effect !== undefined ? effect : node.effect !== undefined ? node.effect : null;
    this.issues.push({ path, code, severity: sev, effect: eff, message: message ?? code });
    this.bad.add(path);
  }

  label(node: SchemaNode, key: string | null): string {
    if (node.label) return node.label;
    if (key === null) return 'Value';
    const text = String(key).replace(/_/g, ' ');
    return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
  }

  withHint(message: string, node: SchemaNode): string {
    if (!node.hint) return message;
    const base = message.endsWith('.') ? message.slice(0, -1) : message;
    return `${base} — ${node.hint}.`;
  }

  msg(code: string, node: SchemaNode, key: string | null, params: Record<string, Json> = {}): string {
    const p = { label: this.label(node, key), ...params };
    return this.withHint(render(this.schema.messages[code], p), node);
  }

  isAbsent(node: SchemaNode, value: Json): boolean {
    if (value === undefined) return true;
    if (value === null && node.nullable) return true;
    return ((node.absent_values as Json[]) || []).some((a) => jsonEqual(value, a));
  }

  node(node: SchemaNode, value: Json, path: string, key: string | null, parent: Json) {
    const ntype = node.type;
    if (ntype === undefined || ntype === 'none') return;
    let required = Boolean(node.required);
    if (!required && node.required_when && isObj(parent)) required = evalCond(node.required_when, parent);
    if (this.isAbsent(node, value)) {
      if (required) {
        const message =
          path === 'args' ? this.schema.messages.args_required : this.msg('required', node, key);
        this.add(path, 'required', node, message);
      }
      return;
    }
    if (ntype === 'any') return;
    if (ntype === 'one_of') {
      this.oneOf(node, value, path, key, parent);
      return;
    }
    const kinds = NODE_KINDS[ntype];
    if (!kinds) return; // unknown node type: skipped (server knows it)
    if (kinds.indexOf(kindOf(value)) < 0) {
      const expected = node.expected || this.schema.expected[ntype] || ntype;
      this.add(path, 'type', node, this.msg('type', node, key, { expected }));
      return;
    }
    switch (ntype) {
      case 'string':
        this.tString(node, value, path, key);
        break;
      case 'boolean':
        this.scalarEnum(node, value, path, key);
        break;
      case 'integer':
        if (!Number.isInteger(value)) {
          this.add(path, 'type', node, this.msg('type', node, key, { expected: this.schema.expected.integer }));
          break;
        }
        this.numeric(node, value, path, key, value);
        break;
      case 'number':
        this.numeric(node, value, path, key, value);
        break;
      case 'number_like': {
        const num = resolveNumber(value);
        if (num === null) {
          this.add(path, 'type', node, this.msg('type', node, key, { expected: this.schema.expected.number_like }));
          break;
        }
        this.numeric(node, num, path, key, num);
        break;
      }
      case 'int_like': {
        const num = resolveInt(value);
        if (num === null) {
          this.add(path, 'type', node, this.msg('type', node, key, { expected: this.schema.expected.int_like }));
          break;
        }
        this.numeric(node, num, path, key, num);
        break;
      }
      case 'id_ref':
        this.tIdRef(node, value, path, key);
        break;
      case 'datetime':
      case 'date':
      case 'time':
        if (parseDt(ntype, value) === null) {
          this.add(path, 'format', node, this.msg('format', node, key, { format_hint: this.schema.format_hints[ntype] }));
        }
        break;
      case 'object':
        this.tObject(node, value, path);
        break;
      case 'list':
        this.tList(node, value, path, key);
        break;
      default:
        break;
    }
    // server_check hooks are server-only.
  }

  oneOf(node: SchemaNode, value: Json, path: string, key: string | null, parent: Json) {
    const kind = kindOf(value);
    const options = node.options as SchemaNode[];
    const candidates = options.filter((o) => (NODE_KINDS[o.type] || []).indexOf(kind) >= 0);
    if (candidates.length === 0) {
      const expected =
        node.expected || options.map((o) => this.schema.expected[o.type] || '').join(' or ');
      this.add(path, 'type', node, this.msg('type', node, key, { expected }));
      return;
    }
    if (candidates.length === 1) {
      const option: SchemaNode = { ...candidates[0] };
      for (const attr of ['label', 'effect']) {
        if (attr in node && !(attr in option)) option[attr] = node[attr];
      }
      this.node(option, value, path, key, parent);
      return;
    }
    for (const option of candidates) {
      const trial = new Run(this.schema);
      trial.node(option, value, path, key, parent);
      if (!trial.issues.some((i) => i.severity === 'error')) {
        this.node(option, value, path, key, parent);
        return;
      }
    }
    this.add(path, 'one_of', node, this.msg('one_of', node, key, { expected: node.expected || 'a valid value' }));
  }

  scalarEnum(node: SchemaNode, resolved: Json, path: string, key: string | null) {
    if (!node.enum) return;
    if ((node.enum as Json[]).some((e) => jsonEqual(resolved, e))) return;
    const labels: Record<string, string> = node.enum_labels || {};
    const options = (node.enum as Json[])
      .map((e) => (labels[String(e)] !== undefined ? labels[String(e)] : String(e)))
      .join(', ');
    this.add(path, 'enum', node, this.msg('enum', node, key, { options }));
  }

  bounds(node: SchemaNode, num: number, path: string, key: string | null): boolean {
    if (node.min !== undefined && num < node.min) {
      this.add(path, 'min', node, this.msg('min', node, key, { min: fmtNum(node.min) }));
      return false;
    }
    if (node.max !== undefined && num > node.max) {
      this.add(path, 'max', node, this.msg('max', node, key, { max: fmtNum(node.max) }));
      return false;
    }
    return true;
  }

  numeric(node: SchemaNode, num: number, path: string, key: string | null, resolved: Json) {
    if (this.bounds(node, num, path, key)) this.scalarEnum(node, resolved, path, key);
  }

  tString(node: SchemaNode, value: string, path: string, key: string | null) {
    if (node.non_blank && !value.trim()) {
      this.add(path, 'non_blank', node, this.msg('non_blank', node, key));
      return;
    }
    if (node.max_length !== undefined && value.length > node.max_length) {
      this.add(path, 'max_length', node, this.msg('max_length', node, key, { max_length: node.max_length }));
      return;
    }
    if (node.pattern !== undefined && !new RegExp(`^(?:${node.pattern})$`).test(value)) {
      const hint = node.pattern_hint || 'in the expected format';
      this.add(path, 'pattern', node, this.msg('pattern', node, key, { pattern_hint: hint }));
      return;
    }
    this.scalarEnum(node, value, path, key);
  }

  tIdRef(node: SchemaNode, value: Json, path: string, key: string | null) {
    let raw = value;
    const typeError = () =>
      this.add(path, 'type', node, this.msg('type', node, key, { expected: this.schema.expected.id_ref }));
    if (isObj(value)) {
      if (!Object.prototype.hasOwnProperty.call(value, 'id')) {
        typeError();
        return;
      }
      raw = value.id;
      if (node.object_id === 'integer' && !(typeof raw === 'number' && Number.isInteger(raw))) {
        typeError();
        return;
      }
    }
    const num = typeof raw === 'boolean' ? null : resolveInt(raw);
    if (num === null) {
      typeError();
      return;
    }
    this.numeric(node, num, path, key, num);
  }

  tObject(node: SchemaNode, value: Json, path: string) {
    const fields: Record<string, SchemaNode> = node.fields || {};
    const order: string[] = node.order || Object.keys(fields);
    for (const fkey of order) {
      const fnode = fields[fkey];
      if (!fnode) continue;
      const v = Object.prototype.hasOwnProperty.call(value, fkey) ? value[fkey] : MISSING;
      this.node(fnode, v, `${path}.${fkey}`, fkey, value);
    }
    for (const check of (node.checks as SchemaNode[]) || []) this.check(check, node, value, path);
  }

  tList(node: SchemaNode, value: Json[], path: string, key: string | null) {
    if (node.min_items !== undefined && value.length < node.min_items) {
      const itemLabel =
        node.item_label || (node.items && node.items.label ? String(node.items.label) : 'entry').toLowerCase();
      this.add(path, 'min_items', node, this.msg('min_items', node, key, { min_items: node.min_items, item_label: itemLabel }));
    } else if (node.max_items !== undefined && value.length > node.max_items) {
      this.add(path, 'max_items', node, this.msg('max_items', node, key, { max_items: node.max_items }));
    }
    const items: SchemaNode | undefined = node.items;
    if (items) value.forEach((item, i) => this.node(items, item, `${path}.${i}`, key, null));
    if (node.unique && items) {
      const seen: Json[] = [];
      for (let i = 0; i < value.length; i++) {
        if (this.bad.has(`${path}.${i}`)) continue;
        const k = uniqueKey(items, value[i]);
        if (seen.some((s) => jsonEqual(k, s))) {
          this.add(path, 'unique', node, this.msg('unique', node, key));
          break;
        }
        seen.push(k);
      }
    }
  }

  check(check: SchemaNode, onode: SchemaNode, value: Json, path: string) {
    if (check.when && !evalCond(check.when, value)) return;
    const fields: Record<string, SchemaNode> = onode.fields || {};
    const ok = (p: string) => getPath(value, p) !== undefined && !this.bad.has(`${path}.${p}`);
    const lbl = (p: string) => this.label(fields[p] || {}, p);
    const m = this.schema.messages;
    if (check.kind === 'assert') {
      const target = check.path ? `${path}.${check.path}` : path;
      if (this.bad.has(target)) return;
      if (!evalCond(check.cond, value)) this.add(target, check.code || 'assert', check, check.message, check.effect);
    } else if (check.kind === 'order') {
      const lo: string = check.lo;
      const hi: string = check.hi;
      if (!(ok(lo) && ok(hi))) return;
      const loV = getPath(value, lo);
      const hiV = getPath(value, hi);
      if (loV === null || hiV === null) return;
      const as: string = check.as || 'number';
      const target = `${path}.${hi}`;
      let a: any;
      let b: any;
      if (as === 'number') {
        a = resolveNumber(loV);
        b = resolveNumber(hiV);
      } else {
        a = parseDt(as, loV);
        b = parseDt(as, hiV);
        if (as === 'datetime' && a !== null && b !== null) {
          if (a[0] !== b[0]) {
            this.add(target, 'tz_mixed', check, render(m.tz_mixed, { lo_label: lbl(lo), hi_label: lbl(hi) }), 'raises');
            return;
          }
          a = a[1];
          b = b[1];
        }
      }
      if (a === null || b === null) return;
      if (a > b) {
        const message = check.message || render(m.order, { lo_label: lbl(lo), hi_label: lbl(hi) });
        this.add(target, check.code || 'order', check, message, check.effect);
      }
    } else if (check.kind === 'product') {
      const paths: string[] = [check.result, ...(check.factors as string[])];
      if (!paths.every(ok)) return;
      const nums = paths.map((p) => resolveNumber(getPath(value, p)));
      if (nums.some((n) => n === null)) return;
      let product = 1;
      for (const n of nums.slice(1)) product *= n as number;
      if (Math.abs(product - (nums[0] as number)) > 0.5) {
        const message =
          check.message ||
          render(m.product, {
            result_label: lbl(check.result),
            factor_labels: (check.factors as string[]).map(lbl).join(' × '),
          });
        this.add(`${path}.${check.result}`, 'product', check, message, check.effect);
      }
    }
    // unknown kinds: skipped
  }
}

/** True when this interpreter can run `schema` (else: skip client validation). */
export function schemaSupported(schema: RuleArgsSchema | null | undefined): schema is RuleArgsSchema {
  return Boolean(schema && typeof schema.version === 'number' && schema.version <= SUPPORTED_SCHEMA_VERSION);
}

export function validateRuleArgs(schema: RuleArgsSchema, tpl: number, args: Json): RuleIssue[] {
  const entry = schema.templates[String(tpl)];
  if (!entry) return [];
  const run = new Run(schema);
  let root = entry.args;
  if (!('label' in root)) root = { ...root, label: 'Settings' };
  const value = (args === null || args === undefined) && !root.nullable ? MISSING : args;
  run.node(root, value, 'args', null, null);
  if (entry.notice) {
    run.issues.push({ path: 'args', code: 'notice', severity: 'warning', effect: null, message: entry.notice });
  }
  return run.issues;
}

/** Row-level checks + the row's args issues (what the server checks without the DB). */
export function validateRuleRow(schema: RuleArgsSchema, row: Record<string, Json>): RuleIssue[] {
  const rs = schema.row;
  const m = rs.messages;
  const issues: RuleIssue[] = [];
  const add = (path: string, code: string, message: string | undefined, effect: string | null = null) =>
    issues.push({ path, code, severity: 'error', effect, message: message ?? code });

  const ruleType = 'type' in row ? row.type : 0;
  const rtype = intOrNull(ruleType);
  if (rtype === null || !(String(rtype) in rs.types)) {
    add('type', 'type', render(m.type, { value: show(ruleType) }));
    return issues;
  }

  let op: number | null = 0;
  if ('op' in row) {
    op = intOrNull(row.op);
    if (op === null || !(String(op) in rs.ops)) add('op', 'op', render(m.op, { value: show(row.op) }));
  }

  let value = row.value;
  if (value !== undefined && value !== null && typeof value !== 'string') {
    add('value', 'value_type', m.value_type);
    value = null;
  } else if (typeof value === 'string' && value.length > rs.max_value_length) {
    add('value', 'value_length', render(m.value_length, { max: rs.max_value_length }));
  }

  if (rtype === 1) {
    const field = row.field;
    if (typeof field !== 'string' || !field.trim()) add('field', 'field_required', m.field_required, 'fail_closed');
    else if (field.length > rs.max_field_length) {
      add('field', 'field_length', render(m.field_length, { max: rs.max_field_length }));
    }
    if (rs.value_optional_ops.indexOf(op as number) < 0 && (typeof value !== 'string' || !value.trim())) {
      add('value', 'value_required', m.value_required, 'raises');
    }
    return issues;
  }

  const rawTpl = row.tpl;
  if (rawTpl === null || rawTpl === undefined || rawTpl === '') {
    add('tpl', 'tpl_required', m.tpl_required, 'fail_closed');
    return issues;
  }
  const tpl = intOrNull(rawTpl);
  if (tpl === null || !(String(tpl) in schema.templates)) {
    add('tpl', 'tpl', render(m.tpl, { value: show(rawTpl) }), 'fail_closed');
    return issues;
  }
  if (tpl === rs.tpl_ruleset) {
    let target: number | null = null;
    try {
      target = nestedRulesetId(row.nested_ruleset);
    } catch {
      target = null;
    }
    if (target === null) add('nested_ruleset', 'nested_required', m.nested_required, 'fail_closed');
  }
  return issues.concat(validateRuleArgs(schema, tpl, row.args));
}

/** The nested-ruleset reference as an id (null when unset); throws on garbage. */
export function nestedRulesetId(raw: Json): number | null {
  if (raw === null || raw === undefined) return null;
  const candidate = isObj(raw) ? raw.id : raw;
  const resolved = intOrNull(candidate);
  if (resolved === null) throw new Error(`not a ruleset reference: ${JSON.stringify(raw)}`);
  return resolved;
}

/** Would the backend funnel write anything different for `row` over `stored`?
 *  `stored` uses the DB shape (`nested_ruleset_id`); keys absent from `row` are
 *  left alone by the funnel, so they count as unchanged. */
export function rowsEqual(stored: Record<string, Json>, row: Record<string, Json>): boolean {
  const sameInt = (k: string) => !(k in row) || intOrNull(row[k]) === (stored[k] === undefined ? null : stored[k]);
  const sameText = (k: string) => !(k in row) || (row[k] || '') === (stored[k] || '');
  if (!(sameInt('type') && sameInt('tpl') && sameInt('op'))) return false;
  if ('negate' in row && Boolean(row.negate) !== Boolean(stored.negate)) return false;
  if (!(sameText('field') && sameText('value'))) return false;
  if ('nested_ruleset' in row) {
    try {
      const stored_id = stored.nested_ruleset_id === undefined ? null : stored.nested_ruleset_id;
      if (nestedRulesetId(row.nested_ruleset) !== stored_id) return false;
    } catch {
      return false;
    }
  }
  if ('args' in row && !jsonEqual(row.args, stored.args === undefined ? null : stored.args)) return false;
  return true;
}

export interface RowIssues {
  /** Blocking: issues on new/changed rows, keyed by row index. */
  errors: Record<number, RuleIssue[]>;
  /** Non-blocking: warnings, plus every issue on rows the save leaves unchanged. */
  warnings: Record<number, RuleIssue[]>;
  blocking: boolean;
}

/** Validate a complete rule list the way the server gates it: issues on rows
 *  that are new (`baseline` has no row for the id) or changed block; issues on
 *  unchanged rows only warn. `idOf(row)` returns the persisted id or null. */
export function validateRuleList(
  schema: RuleArgsSchema,
  rows: Record<string, Json>[],
  baseline: Record<number, Record<string, Json>>,
  idOf: (row: Record<string, Json>) => number | null = (row) => (typeof row.id === 'number' ? row.id : null),
): RowIssues {
  const out: RowIssues = { errors: {}, warnings: {}, blocking: false };
  rows.forEach((row, idx) => {
    const issues = validateRuleRow(schema, row);
    if (!issues.length) return;
    const id = idOf(row);
    const stored = id !== null ? baseline[id] : undefined;
    const changed = stored === undefined || !rowsEqual(stored, row);
    for (const issue of issues) {
      const bucket = changed && issue.severity === 'error' ? out.errors : out.warnings;
      (bucket[idx] = bucket[idx] || []).push(issue);
    }
  });
  out.blocking = Object.keys(out.errors).length > 0;
  return out;
}
